/**
 * Maps API/snake_case field names to user-friendly labels for validation messages.
 */
const FIELD_LABELS = {
    user_email: "Email",
    user_password: "Password",
    email: "Email",
    password: "Password",
    current_password: "Current password",
    new_password: "New password",
    confirm_password: "Confirm password",
    verification_code: "Verification code",
  };
  
  /**
   * Simplifies common Pydantic/validation error phrases to user-friendly text.
   */
  const PHRASE_REPLACEMENTS = [
    [
      /value is not a valid email address/i,
      "Please enter a valid email address.",
    ],
    [
      /the part after the @-sign contains invalid characters/i,
      "The domain part of your email is invalid (e.g. use .com not ,com).",
    ],
    [
      /value is not a valid (?:email )?address/i,
      "Please enter a valid email address.",
    ],
    [
      /field required/i,
      "This field is required.",
    ],
    [
      /ensure this value has at least \d+ character/i,
      "Please enter a longer value.",
    ],
    [
      /ensure this value has at most \d+ character/i,
      "Please enter a shorter value.",
    ],
  ];
  
  /**
   * Strips the technical suffix from Pydantic error strings, e.g.:
   * "...message. [type=value_error, input_value='x', input_type=str]"
   */
  function stripTechnicalSuffix(str) {
    const bracketStart = str.indexOf(" [type=");
    if (bracketStart !== -1) {
      return str.slice(0, bracketStart).trim();
    }
    return str.trim();
  }
  
  /**
   * Extracts a clean message from "N validation error(s) for ModelName field_name message"
   * and optionally prefixes with a friendly field label.
   */
  function parseValidationErrorText(text) {
    let cleaned = stripTechnicalSuffix(text);
  
    // Match: "1 validation error for LoginRequest user_email value is not..."
    const prefixMatch = cleaned.match(
      /^\d+\s+validation\s+error(?:s)?\s+for\s+\w+\s+(\w+)\s+(.+)$/i
    );
    if (prefixMatch) {
      const [, field, rest] = prefixMatch;
      const label = FIELD_LABELS[field] || field.replace(/_/g, " ");
      const restTrimmed = rest.trim();
      for (const [pattern, replacement] of PHRASE_REPLACEMENTS) {
        if (pattern.test(restTrimmed)) {
          return `${label}: ${replacement}`;
        }
      }
      return `${label}: ${restTrimmed}`;
    }
  
    // Try phrase replacements on the whole message
    for (const [pattern, replacement] of PHRASE_REPLACEMENTS) {
      if (pattern.test(cleaned)) {
        return replacement;
      }
    }
  
    return cleaned;
  }
  
  /**
   * Formats a single error item: string (Pydantic style) or object (FastAPI 422 detail item).
   */
  function formatOne(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") {
      return parseValidationErrorText(detail);
    }
    if (typeof detail === "object") {
      const msg = detail.msg ?? detail.message ?? detail.detail;
      if (typeof msg === "string") return parseValidationErrorText(msg);
  
      const loc = detail.loc;
      const field = Array.isArray(loc) ? loc[loc.length - 1] : null;
      const label = field && FIELD_LABELS[field] ? FIELD_LABELS[field] : field;
      const rawMsg = typeof msg === "string" ? msg : (detail.msg ?? detail.message ?? null);
      const text = typeof rawMsg === "string" ? rawMsg : (field ? `Invalid value` : JSON.stringify(detail));
      const parsed = parseValidationErrorText(text);
      return label ? `${label}: ${parsed}` : parsed;
    }
    return String(detail);
  }
  
  /**
   * Formats API error detail into a user-friendly message.
   * Handles:
   * - Pydantic-style string: "1 validation error for LoginRequest user_email value is not..."
   * - FastAPI 422 array of { loc, msg, type }
   * - Plain strings and objects with message/msg
   *
   * @param {string|Array|object} errorDetail - response.data.detail or similar
   * @param {string} fallback - message when detail is empty
   * @returns {string} User-friendly error message
   */
  export function formatValidationError(errorDetail, fallback = "Something went wrong. Please try again.") {
    if (errorDetail == null || errorDetail === "") return fallback;
  
    if (Array.isArray(errorDetail)) {
      const messages = errorDetail.map((item) => formatOne(item)).filter(Boolean);
      if (messages.length === 0) return fallback;
      return messages.length === 1 ? messages[0] : messages.join(" ");
    }
  
    if (typeof errorDetail === "string") {
      const parsed = parseValidationErrorText(errorDetail);
      return parsed || fallback;
    }
  
    if (typeof errorDetail === "object") {
      const msg = errorDetail.msg ?? errorDetail.message ?? errorDetail.detail;
      if (msg != null) return formatValidationError(msg, fallback);
      return fallback;
    }
  
    return String(errorDetail);
  }
  